import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEmailPanel } from '../../pages/mail/contexts/EmailPanelContext';
import { useWebsiteLeads } from '../../contexts/WebsiteLeadsContext';
import NewCustomerModal from '../../pages/customers/NewCustomerModal';
import NewJobModal from '../../pages/job-summary/NewJobModal';
import PasswordManagerModal from '../../modals/PasswordManagerModal';
import AgentAccessModal from '../../modals/AgentAccessModal';
import { getAgentAccessConfig } from '../../pages/marketing/googleAdsApi';
import { getAuth, onIdTokenChanged } from 'firebase/auth';
import { useAndreiChat } from '../../contexts/AndreiChatContext';
import { useApprovals } from '../../contexts/ApprovalsContext';
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { RecentConversationsPopover } from '../terminal/RecentConversationsPopover';
import { useThemeCustomizer } from '../../contexts/ThemeCustomizerContext';
import { setActiveThemeId } from '../../services/themeCustomizerService';
import { Icon } from './icons';

interface GraphiteHeaderProps {
  onMenuToggle: () => void;
  mobileMenuOpen: boolean;
}

// Friendly section title for the current route (Apple shows a clean page name,
// not a shell prompt). Longest-prefix match, else humanise the last segment.
const TITLES: Array<[string, string]> = [
  ['/marketing/google-ads', 'Google Ads'],
  ['/accounting/cashflow', 'Cash Flow'],
  ['/accounting', 'Invoices & Bills'],
  ['/banking', 'Bank Reconciliation'],
  ['/website-submissions', 'Website Submissions'],
  ['/analytics', 'Analytics'],
  ['/contacts', 'Contacts'],
  ['/monthly-support', 'Monthly Support'],
  ['/user/account/settings', 'Settings'],
  ['/dashboard', 'Dashboard'],
];
function pageTitle(pathname: string): string {
  for (const [prefix, title] of TITLES) {
    if (pathname === prefix || pathname.startsWith(prefix + '/') || pathname.startsWith(prefix)) return title;
  }
  const seg = pathname.replace(/^\/+|\/+$/g, '').split('/').pop() || 'Dashboard';
  return seg.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Icon header action — round-square button with an optional count badge. */
interface ActionProps {
  icon: string;
  label: string;
  onClick: () => void;
  count?: number;
  countTone?: 'primary' | 'amber' | 'danger';
  flash?: boolean;
}
const Action = React.forwardRef<HTMLButtonElement, ActionProps>(
  ({ icon, label, onClick, count, countTone, flash }, ref) => (
    <button ref={ref} type="button" className="g-header__action" onClick={onClick} title={label} aria-label={label}>
      <Icon name={icon} size={19} />
      {count !== undefined && count > 0 && (
        <span className={clsx('g-header__badge', {
          'g-header__badge--primary': countTone === 'primary',
          'g-header__badge--amber': countTone === 'amber',
          'g-header__badge--danger': countTone === 'danger',
          'g-header__badge--flash': flash,
        })}>{count > 99 ? '99+' : count}</span>
      )}
    </button>
  ),
);
Action.displayName = 'GraphiteAction';

const GraphiteHeader: React.FC<GraphiteHeaderProps> = ({ onMenuToggle, mobileMenuOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const title = pageTitle(location.pathname);

  // Same context hooks the classic HeaderWrapper / TerminalHeader use, so
  // counts + active states stay in sync whichever chrome is rendered.
  const { openEmailPanel, unreadBadgeCount, activeSyncJob } = useEmailPanel();
  const { newLeadsCount, openLeadsPanel } = useWebsiteLeads();
  const { openAndreiChat } = useAndreiChat();
  const { openApprovals, count: approvalsCount, flash: approvalsFlash } = useApprovals();
  const { themes, activeThemeId } = useThemeCustomizer();

  const [showThemes, setShowThemes] = useState(false);
  const [showNewJob, setShowNewJob] = useState(false);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [showAgentAccess, setShowAgentAccess] = useState(false);
  const [showMobileOverflow, setShowMobileOverflow] = useState(false);
  const [isAgentAccessOwner, setIsAgentAccessOwner] = useState(false);

  // Recent-conversations popover, anchored to the [chat] button via a ref.
  const [showRecentConvos, setShowRecentConvos] = useState(false);
  const chatBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const [chatAnchorRect, setChatAnchorRect] = useState<DOMRect | null>(null);
  const toggleRecentConvos = () => {
    const next = !showRecentConvos;
    if (next && chatBtnRef.current) setChatAnchorRect(chatBtnRef.current.getBoundingClientRect());
    setShowRecentConvos(next);
  };

  // [andrei N] pending-action badge — live, capped at 50 docs.
  const [pendingCount, setPendingCount] = useState<number>(0);
  useEffect(() => {
    const q = query(collection(db, 'agent_pending_actions'), where('status', '==', 'pending'), limit(50));
    const unsub = onSnapshot(q, (snap) => setPendingCount(snap.size), () => { /* silent */ });
    return () => unsub();
  }, []);

  // Window-event bridges (shared with the other chromes).
  useEffect(() => {
    const cal = () => navigate('/calendar');
    const access = () => setShowAgentAccess(true);
    window.addEventListener('open-calendar-modal', cal);
    window.addEventListener('approvals:open-agent-access', access);
    return () => {
      window.removeEventListener('open-calendar-modal', cal);
      window.removeEventListener('approvals:open-agent-access', access);
    };
  }, [navigate]);

  // Owner-only gate for [access].
  useEffect(() => {
    const unsub = onIdTokenChanged(getAuth(), async (u) => {
      if (!u) { setIsAgentAccessOwner(false); return; }
      try { const r = await getAgentAccessConfig(); setIsAgentAccessOwner(!!r?.ok); }
      catch { setIsAgentAccessOwner(false); }
    });
    return () => unsub();
  }, []);

  const syncProgressPct =
    activeSyncJob?.status === 'completed' ? 100
    : activeSyncJob?.percent ?? (activeSyncJob?.status === 'pending' ? 10 : 60);

  // Quick theme picker — "Classic" (no active theme = default Metronic) plus the
  // built-in + custom themes. Switching writes the per-user preference; the
  // ThemeCustomizerContext subscription re-applies it (and swaps the chrome).
  const themeOptions = useMemo(() => [
    { id: null as string | null, name: 'Classic', swatch: '#009ef7' },
    ...themes.map((t) => ({
      id: t.id as string | null,
      name: t.name || (t as any).slug || 'Theme',
      swatch: (t as any).swatch || (t.overrides as any)?.light?.['--bs-primary'] || '#8a857c',
    })),
  ], [themes]);

  const actions = (
    <>
      <Action icon="job"    label="New job"           onClick={() => setShowNewJob(true)} />
      <Action icon="cust"   label="New customer"      onClick={() => setShowNewCustomer(true)} />
      <Action icon="cal"    label="Calendar"          onClick={() => navigate('/calendar')} />
      <Action icon="lock"   label="Password manager"  onClick={() => setShowPasswords(true)} />
      <Action ref={chatBtnRef} icon="chat" label="Recent conversations" onClick={toggleRecentConvos} />
      <Action icon="leads"  label="Website activity"  onClick={openLeadsPanel}  count={newLeadsCount}    countTone="amber" />
      <Action icon="mail"   label="Email feed"        onClick={() => openEmailPanel('feed')} count={unreadBadgeCount} countTone="primary" />
      <Action icon="andrei" label="Chat with Andrei"  onClick={() => openAndreiChat()} count={pendingCount} countTone={pendingCount > 10 ? 'danger' : 'amber'} />
      <Action icon="approve" label="Approvals inbox"  onClick={openApprovals} count={approvalsCount} countTone={approvalsFlash ? 'danger' : 'amber'} flash={approvalsFlash} />
      <Action icon="theme"   label="Theme"            onClick={() => setShowThemes((s) => !s)} />
      {isAgentAccessOwner && (
        <Action icon="access" label="Agent access" onClick={() => setShowAgentAccess(true)} />
      )}
    </>
  );

  return (
    <>
      <header className="g-header">
        {activeSyncJob && (
          <div className={clsx('g-header__sync', `g-header__sync--${activeSyncJob.status}`)}
            style={{ width: `${syncProgressPct}%` }} title={activeSyncJob.progress || 'Syncing mail…'} />
        )}

        <div className="g-header__left">
          <button type="button" className="g-header__menu" onClick={onMenuToggle}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen}>
            <Icon name={mobileMenuOpen ? 'close' : 'menu'} size={20} />
          </button>
          <h1 className="g-header__title">{title}</h1>
        </div>

        <div className="g-header__right">
          <div className="g-header__actions g-header__actions--desktop">{actions}</div>
          {/* Andrei is the primary mobile surface — promote it OUT of the
              overflow so it's one tap (not tap-overflow-then-tap). Visible only
              where the desktop action row is collapsed (≤899px); its badge
              still shows the pending-action count. */}
          <span className="g-header__andrei-quick">
            <Action icon="andrei" label="Chat with Andrei" onClick={() => openAndreiChat()}
              count={pendingCount} countTone={pendingCount > 10 ? 'danger' : 'amber'} />
          </span>
          <button type="button" className="g-header__overflow"
            onClick={() => setShowMobileOverflow((s) => !s)} aria-expanded={showMobileOverflow} aria-label="More actions">
            <Icon name={showMobileOverflow ? 'close' : 'menu'} size={18} />
          </button>
        </div>

        {showMobileOverflow && (
          <div className="g-header__overflow-sheet" role="menu"
            onClick={(e) => { if ((e.target as HTMLElement).closest('.g-header__action')) setShowMobileOverflow(false); }}>
            {actions}
          </div>
        )}

        {showThemes && (
          <>
            <div className="g-header__sheet-backdrop" onClick={() => setShowThemes(false)} />
            <div className="g-header__theme-sheet" role="menu" aria-label="Theme">
              <div className="g-header__theme-head">Theme</div>
              {themeOptions.map((opt) => {
                const active = (opt.id || null) === (activeThemeId || null);
                return (
                  <button
                    key={opt.id || 'default'}
                    type="button"
                    role="menuitemradio"
                    aria-checked={active}
                    className={clsx('g-header__theme-item', { 'is-active': active })}
                    onClick={() => { setActiveThemeId(opt.id); setShowThemes(false); }}
                  >
                    <span className="g-header__theme-swatch" style={{ background: opt.swatch }} />
                    <span className="g-header__theme-name">{opt.name}</span>
                    {active && <Icon name="approve" size={15} className="g-header__theme-check" />}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </header>

      {/* Reused shared modals — single source of truth; Bootstrap vars are
          covered by the graphite override map so they render in-theme. */}
      {showNewJob && <NewJobModal show={showNewJob} onClose={() => setShowNewJob(false)} />}
      {showNewCustomer && <NewCustomerModal show={showNewCustomer} onClose={() => setShowNewCustomer(false)} customer={null} />}
      {showPasswords && <PasswordManagerModal onClose={() => setShowPasswords(false)} />}
      {showAgentAccess && <AgentAccessModal onClose={() => setShowAgentAccess(false)} />}

      <RecentConversationsPopover show={showRecentConvos} onClose={() => setShowRecentConvos(false)} anchorRect={chatAnchorRect} />
    </>
  );
};

export default GraphiteHeader;
